<template>
  <div class="message-tool-calls">
    <CompactCollapsibleSection
      :label="toolCallSummary(calls)"
      :expanded="state.isGroupExpanded()"
      @toggle="state.toggleGroup()"
    >
      <div class="tool-call-group-details">
        <div v-for="(call, callIdx) in calls" :key="callIdx" class="tool-call">
          <CompactCollapsibleSection
            :label="call.name"
            label-style="body"
            :expanded="state.isCallExpanded(callIdx)"
            @toggle="state.toggleCall(callIdx)"
          >
            <div class="tool-call-details">
              <div class="tool-call-section">
                <span class="tool-call-label">Arguments</span>
                <pre class="tool-call-pre">{{
                  formatJson(call.arguments)
                }}</pre>
              </div>
              <div class="tool-call-section">
                <span class="tool-call-label">Result</span>
                <pre class="tool-call-pre">{{ call.result }}</pre>
              </div>
            </div>
          </CompactCollapsibleSection>
        </div>
      </div>
    </CompactCollapsibleSection>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { OpenaiToolCall } from "@ctrl/controller-rpc";

import CompactCollapsibleSection from "@/components/ai/CompactCollapsibleSection.vue";
import {
  createToolCallGroupState,
  type ToolCallGroupState,
} from "@/components/ai/inFlightSends";

export default defineComponent({
  name: "ConversationToolCalls",
  components: { CompactCollapsibleSection },
  props: {
    calls: { type: Array as PropType<OpenaiToolCall[]>, required: true },
    toolCallState: {
      type: Object as PropType<ToolCallGroupState | undefined>,
      default: undefined,
    },
  },
  setup(props) {
    const state = props.toolCallState ?? createToolCallGroupState();

    const formatJson = (value: unknown): string => {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    };

    const toolCallSummary = (calls: OpenaiToolCall[]): string => {
      const toolCount = calls.length;
      const toolLabel = toolCount === 1 ? "tool" : "tools";
      const uniqueNames = Array.from(new Set(calls.map((call) => call.name)));
      const visibleNames = uniqueNames.slice(0, 3);
      const extraCount = uniqueNames.length - visibleNames.length;
      const names = visibleNames.join(", ");
      if (names.length === 0) return `Called ${toolCount} ${toolLabel}`;
      const extra = extraCount > 0 ? `, +${extraCount} more` : "";
      return `Called ${toolCount} ${toolLabel}: ${names}${extra}`;
    };

    return {
      state,
      formatJson,
      toolCallSummary,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/agent" as *;

.message-tool-calls {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;

  .tool-call {
    display: flex;
    flex-direction: column;
  }

  .tool-call-group-details {
    display: flex;
    flex-direction: column;
    margin-top: 4px;
    margin-inline-start: 20px;
  }

  .tool-call-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
    margin-inline-start: 20px;
  }

  .tool-call-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tool-call-label {
    @extend %font-caption-regular;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    @include themed() {
      color: t($grey-40);
    }
  }

  .tool-call-pre {
    @extend %font-body-regular;
    font-family: monospace;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    padding: 8px 10px;
    border-radius: $border-radius-s;
    user-select: text;
    cursor: text;

    &::selection,
    *::selection {
      @include themed() {
        background-color: t($tertiary-20);
      }
    }

    @include themed() {
      background-color: t($bg-alt);
      color: t($grey-80);
    }
  }
}
</style>
